/**
 * ComplianceCheckPanel — auto-runs requirement check when HS code is available.
 * Embedded inside extraction result modals after upload.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Alert, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Collapse,
  IconButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  AutoAwesome as AIIcon,
  Rule as RuleIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIconBtn,
} from '@mui/icons-material';
import { checkRequirements, RequirementCheckResponse } from '../../../services/supplierDeclarationService';
import { brand } from '../../../theme';

interface ComplianceCheckPanelProps {
  hsCode?: string;
  originCountry?: string;
  productDescription?: string;
  supplierId?: string;
}

const typeLabel: Record<string, string> = {
  origin: 'Origin Declaration',
  preferential: 'Preferential Origin',
  quality: 'Quality Certificate',
  safety: 'Safety Declaration',
  environmental: 'Environmental Declaration',
  other: 'Other',
};

const ComplianceCheckPanel: React.FC<ComplianceCheckPanelProps> = ({
  hsCode,
  originCountry,
  productDescription,
  supplierId,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RequirementCheckResponse | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (!hsCode) return;
    runCheck();
  }, [hsCode, originCountry, supplierId]);

  const runCheck = async () => {
    if (!hsCode) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await checkRequirements(
        hsCode,
        originCountry || undefined,
        supplierId || undefined,
        productDescription || undefined,
      );
      setResult(res);
    } catch {
      // silently fail — don't block the main upload confirmation flow
    } finally {
      setLoading(false);
    }
  };

  if (!hsCode) return null;

  return (
    <Box sx={{ mt: 2, border: `1px solid ${brand.borderSubtle}`, borderRadius: 2, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, bgcolor: '#fff8f0', cursor: 'pointer',
          borderBottom: expanded ? `1px solid ${brand.borderSubtle}` : 'none',
        }}
        onClick={() => setExpanded(p => !p)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon sx={{ color: brand.orange, fontSize: 18 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ color: brand.textPrimary }}>
            Compliance Check
          </Typography>
          {loading && <CircularProgress size={14} sx={{ color: brand.orange }} />}
          {result && !loading && (
            <>
              <Chip icon={<CheckIcon />} label={`${result.satisfied} OK`}
                size="small" color="success" sx={{ height: 20, fontSize: 11 }} />
              {result.missing > 0 && (
                <Chip icon={<MissingIcon />} label={`${result.missing} Missing`}
                  size="small" color="error" sx={{ height: 20, fontSize: 11 }} />
              )}
            </>
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <CollapseIconBtn fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Body */}
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {loading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: brand.orange }} />
              <Typography variant="body2" color="text.secondary">
                Checking declaration requirements for HS {hsCode}…
              </Typography>
            </Box>
          )}

          {!loading && !result && (
            <Typography variant="body2" color="text.secondary">
              No HS code available to check.
            </Typography>
          )}

          {!loading && result && result.requirements.length === 0 && (
            <Alert severity="success" sx={{ py: 0.5 }}>
              No declaration requirements found for HS {result.hs_code}
              {result.origin_country ? ` from ${result.origin_country}` : ''}.
            </Alert>
          )}

          {!loading && result && result.requirements.length > 0 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Requirements for HS <strong>{result.hs_code}</strong>
                {result.origin_country ? ` · Origin: ${result.origin_country}` : ''}
                {result.supplier_name ? ` · Supplier: ${result.supplier_name}` : ''}
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ width: 40 }}></TableCell>
                      <TableCell><strong>Required Declaration</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Source</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.requirements.map((item, idx) => (
                      <TableRow
                        key={idx}
                        sx={{ bgcolor: item.satisfied ? '#f0fdf4' : item.is_mandatory ? '#fff1f1' : '#fffbeb' }}
                      >
                        <TableCell>
                          {item.satisfied
                            ? <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
                            : <MissingIcon sx={{ color: item.is_mandatory ? 'error.main' : 'warning.main', fontSize: 18 }} />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {typeLabel[item.declaration_type] ?? item.declaration_type}
                          </Typography>
                          {item.satisfied && item.declaration_status && (
                            <Typography variant="caption" color="success.main">
                              Active · {item.declaration_status}
                            </Typography>
                          )}
                          {!item.satisfied && (
                            <Typography variant="caption" color={item.is_mandatory ? 'error.main' : 'warning.main'}>
                              {item.is_mandatory ? 'MISSING — must request from supplier' : 'Optional — not provided'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.is_mandatory
                            ? <Chip label="Mandatory" size="small" color="error" />
                            : <Chip label="Optional" size="small" />}
                        </TableCell>
                        <TableCell>
                          {item.source === 'ai'
                            ? <Chip icon={<AIIcon />} label="AI" size="small" color="info" />
                            : <Chip icon={<RuleIcon />} label="Rule" size="small" variant="outlined" />}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                          {item.description || '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ComplianceCheckPanel;
